import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown, Sparkles } from 'lucide-react';
import { HERO, ASSETS } from '@/data/content';
import { EASE } from '@/lib/motion';

const lineColors = ['text-white', 'text-transparent bg-clip-text bg-gradient-to-r from-brand-goldlight via-brand-gold to-brand-goldlight', 'text-white'];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const posterY = useTransform(scrollYProgress, [0, 1], ['0%', '12%']);

  return (
    <section id="home" ref={ref} className="relative overflow-hidden bg-night" data-testid="hero-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(197,160,89,0.08),transparent_55%)] pointer-events-none" />

      <div className="relative pt-20 sm:pt-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.1, ease: EASE }}
          className="mx-auto max-w-md sm:max-w-xl lg:max-w-2xl px-4"
        >
          <motion.div
            style={{ y: posterY }}
            className="relative overflow-hidden rounded-sm gold-hairline shadow-[0_0_90px_rgba(230,57,70,0.18)]"
          >
            <img src={ASSETS.heroPoster} alt="Abu Zama Soccer Academy — official squad" className="w-full h-auto block" data-testid="hero-poster" />
            <span className="absolute inset-y-0 w-1/4 bg-gradient-to-r from-transparent via-brand-gold/25 to-transparent animate-gold-sweep pointer-events-none" aria-hidden="true" />
          </motion.div>
        </motion.div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 mt-10 sm:mt-14 pb-20 text-center">
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25, ease: EASE }}
            className="flex items-center justify-center gap-2 text-brand-gold text-xs sm:text-sm tracking-[0.3em] uppercase font-semibold mb-6"
            data-testid="hero-eyebrow"
          >
            <Sparkles size={14} /> Abu Zama Soccer Academy — Canton, Michigan
          </motion.p>

          <h1 className="font-display leading-[0.92] tracking-tight text-[13vw] sm:text-7xl lg:text-8xl uppercase" data-testid="hero-headline">
            {HERO.headline.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${lineColors[i]}`}
                  initial={{ y: '110%' }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.45 + i * 0.16, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
            className="mt-6 max-w-xl mx-auto text-base sm:text-lg text-gray-300 leading-relaxed"
            data-testid="hero-subheadline"
          >
            {HERO.subheadline}
            <span className="block mt-2 text-brand-gold font-semibold text-sm sm:text-base tracking-wide">{HERO.supporting}</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.3, ease: EASE }}
            className="mt-9 flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link to="/register" data-testid="hero-cta-start-journey"
              className="bg-brand-red hover:bg-brand-redhover text-white text-center font-display tracking-widest text-xl sm:text-2xl px-10 py-4 rounded-sm transition-colors duration-300 shadow-[0_0_40px_rgba(230,57,70,0.35)]">
              START YOUR JOURNEY
            </Link>
            <Link to="/register?program=Player%20Evaluation" data-testid="hero-cta-book-evaluation"
              className="border border-brand-gold/70 text-brand-gold hover:bg-brand-gold hover:text-night text-center font-display tracking-widest text-xl sm:text-2xl px-10 py-4 rounded-sm transition-colors duration-300">
              BOOK A PLAYER EVALUATION
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.55 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs sm:text-sm text-gray-400 tracking-widest uppercase"
            data-testid="hero-trust-line"
          >
            {HERO.trustLine.map((t, i) => (
              <span key={t} className="flex items-center gap-4">
                {i > 0 && <span className="w-1 h-1 rounded-full bg-brand-gold" />}
                {t}
              </span>
            ))}
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gray-500"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}>
          <ChevronDown size={22} />
        </motion.div>
      </motion.div>
    </section>
  );
}
